import { useCallback, useEffect } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { PixType } from '../../enums';
import { formatPhoneNumber } from '../../utils/pixFormatters';
import { usePixKeyValidation } from '../usePixKeyValidation';
export function usePixKeyInput(props) {
    const { selectedKeyType, value, onChange, onValidationChange, onInputTouched, userTaxIdentifier = '' } = props;
    const { t } = useTranslation();
    const { validationResult, getPlaceholder, getInputType, isDisabled, markAsTouched } = usePixKeyValidation(selectedKeyType, value);
    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(validationResult.isValid);
        }
    }, [validationResult.isValid, onValidationChange]);
    useEffect(() => {
        if (selectedKeyType === PixType.TAX_IDENTIFIER && userTaxIdentifier) {
            onChange(userTaxIdentifier);
        }
    }, [selectedKeyType, userTaxIdentifier, onChange]);
    const handleInputChange = useCallback((event) => {
        const newValue = event.target.value;
        if (selectedKeyType === PixType.TAX_IDENTIFIER) {
            return;
        }
        if (selectedKeyType === PixType.PHONE) {
            const formattedValue = formatPhoneNumber(newValue);
            onChange(formattedValue);
        }
        else {
            onChange(newValue);
        }
    }, [selectedKeyType, onChange]);
    const handleInputBlur = useCallback(() => {
        markAsTouched();
        if (onInputTouched) {
            onInputTouched();
        }
    }, [markAsTouched, onInputTouched]);
    const getStepTitle = useCallback(() => {
        switch (selectedKeyType) {
            case PixType.EMAIL:
                return t('pix-keys.creation.email-step-title');
            case PixType.PHONE:
                return t('pix-keys.creation.phone-step-title');
            case PixType.TAX_IDENTIFIER:
                return t('pix-keys.creation.tax-identifier-step-title');
            case PixType.RANDOM:
                return t('pix-keys.creation.random-step-title');
            default:
                return '';
        }
    }, [selectedKeyType, t]);
    const getInputLabel = useCallback(() => {
        switch (selectedKeyType) {
            case PixType.EMAIL:
                return t('pix-keys.email');
            case PixType.PHONE:
                return t('pix-keys.phone');
            case PixType.TAX_IDENTIFIER:
                return t('pix-keys.cpf-cnpj');
            default:
                return '';
        }
    }, [selectedKeyType, t]);
    const getInputDataTestId = useCallback(() => {
        switch (selectedKeyType) {
            case PixType.EMAIL:
                return 'EmailInput';
            case PixType.PHONE:
                return 'PhoneInput';
            case PixType.TAX_IDENTIFIER:
                return 'TaxIdentifierInput';
            default:
                return 'Input';
        }
    }, [selectedKeyType]);
    const shouldRenderInputField = useCallback(() => {
        return selectedKeyType !== PixType.RANDOM;
    }, [selectedKeyType]);
    return {
        validationResult,
        getPlaceholder,
        getInputType,
        isDisabled,
        handleInputChange,
        handleInputBlur,
        getStepTitle,
        getInputLabel,
        getInputDataTestId,
        shouldRenderInputField,
    };
}
